type CachedResponse = {
  expiresAt: number;
  status: number;
  statusText: string;
  headers: [string, string][];
  body: ArrayBuffer;
};

const responseCache = new Map<string, Promise<CachedResponse>>();
const CACHE_TTL_MS = 2 * 60 * 1000;

function cacheable(input: RequestInfo | URL, init?: RequestInit): boolean {
  const method = (init?.method || (input instanceof Request ? input.method : "GET")).toUpperCase();
  const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  return method === "GET" && !url.endsWith("/session") && !url.includes("/health") && !url.includes("/ready");
}

function responseFrom(entry: CachedResponse): Response {
  return new Response(entry.body.slice(0), {
    status: entry.status,
    statusText: entry.statusText,
    headers: entry.headers,
  });
}

export function installGetResponseCache(): void {
  const marker = "__netivGetCacheInstalled";
  const scope = window as unknown as Window & Record<string, unknown>;
  if (scope[marker]) return;
  scope[marker] = true;
  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    if (!cacheable(input, init)) return nativeFetch(input, init);
    const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
    const key = `${url}|${init?.credentials || "same-origin"}`;
    const existing = responseCache.get(key);
    if (existing) {
      const cached = await existing;
      if (cached.expiresAt > Date.now()) return responseFrom(cached);
      responseCache.delete(key);
    }

    const request = (async () => {
      const detachedInit = {...init};
      delete detachedInit.signal;
      const response = await nativeFetch(input, detachedInit);
      const headers: [string, string][] = [];
      response.headers.forEach((value, name) => headers.push([name, value]));
      const entry: CachedResponse = {
        expiresAt: Date.now() + CACHE_TTL_MS,
        status: response.status,
        statusText: response.statusText,
        headers,
        body: await response.arrayBuffer(),
      };
      if (!response.ok) responseCache.delete(key);
      return entry;
    })();
    responseCache.set(key, request);
    try {
      return responseFrom(await request);
    } catch (error) {
      responseCache.delete(key);
      throw error;
    }
  };
}